import { useState } from "react";
import { describeAvailability, type ComputerSummary } from "@foreman/app-core";
import type { PowerSettings } from "@foreman/protocol";
import { Chevron, Tile, YouIcon } from "./YouParts";
import { STATUS, Spinner, useApp, useConnection, useHubSummaries } from "../ui";

const OS: Record<string, string> = { mac: "Mac", windows: "Windows", linux: "Linux" };
const TIMEOUTS: { min: number; label: string }[] = [
  { min: 0, label: "Never" },
  { min: 60, label: "1 hour" },
  { min: 240, label: "4 hours" },
  { min: 480, label: "8 hours" },
];

/** One switch: a label, a short explanation, and what the computer says about it. */
function Toggle({ label, hint, on, onChange, disabled, note }: { label: string; hint: string; on: boolean; onChange: (v: boolean) => void; disabled?: boolean; note?: string }) {
  return (
    <div className="ra-setting">
      <div className="switch-row">
        <button className="switch" role="switch" aria-checked={on} disabled={disabled} onClick={() => onChange(!on)}>
          <span className="track">
            <span className="knob" />
          </span>
          <span>{label}</span>
        </button>
      </div>
      <div className="ra-hint">{hint}</div>
      {note && <div className="ra-note">{note}</div>}
    </div>
  );
}

const WAKE_STEPS: Record<string, string> = {
  mac: "On the Mac, open System Settings, then Battery (or Energy), then Options, and turn on “Wake for network access”. Wake works best on power and over Ethernet or the same Wi-Fi.",
  windows: "On the PC, open Device Manager, your network adapter, Power Management, and allow it to wake the computer (magic packet only). Wake-on-LAN may also need turning on in the BIOS or UEFI. It is most reliable over Ethernet.",
};

/** The settings of one computer, shown only for what that computer says it can do. */
function Settings({ summary }: { summary: ComputerSummary }) {
  const { conn, snap } = useConnection(summary.computer.id);
  const power = snap.power;
  const pending = conn?.pendingPower() ?? null;
  const shown: PowerSettings | null = pending ?? power?.settings ?? null;
  const change = (patch: Partial<PowerSettings>) => shown && conn?.setPower({ ...shown, ...patch });

  if (snap.powerUnsupported) return <div className="ra-msg">This computer runs an older Brocoded. Update it to choose how it stays available.</div>;
  if (!power || !shown) return <div className="ra-msg"><Spinner /> Asking your computer…</div>;
  if (!power.supported) return <div className="ra-msg">Brocoded can't keep this kind of computer awake. Mac and Windows computers are supported.</div>;

  const state =
    pending ? "Waiting for your computer to confirm…"
    : power.reason === "active" ? (power.until ? `Active until ${new Date(power.until).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : "Active: this computer stays awake")
    : power.reason === "on_battery" ? "Waiting: this computer is on battery"
    : power.reason === "timeout" ? "Turned off: the time you chose ran out"
    : power.reason === "failed" ? "Couldn't apply: the system refused. Turn it off and on again."
    : undefined;

  return (
    <div className="ra-settings">
      <Toggle label="Keep available while plugged in" hint="Stops the computer from going to sleep when idle. The screen can still turn off and lock." on={shown.keepAwake} onChange={(v) => change({ keepAwake: v })} note={shown.keepAwake ? state : undefined} />
      {power.hasBattery && (
        <Toggle label="Also keep available on battery" hint="Uses more battery. Off by default." on={shown.onBattery} onChange={(v) => change({ onBattery: v })} disabled={!shown.keepAwake} />
      )}
      {shown.keepAwake && (
        <div className="ra-setting">
          <div className="ra-label" id={"ra-timeout-" + summary.computer.id}>Turn off after</div>
          <div className="ra-chips" role="radiogroup" aria-labelledby={"ra-timeout-" + summary.computer.id}>
            {TIMEOUTS.map((t) => (
              <button key={t.min} className={"chip" + (shown.timeoutMin === t.min ? " on" : "")} role="radio" aria-checked={shown.timeoutMin === t.min} onClick={() => change({ timeoutMin: t.min })}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {power.wake.mac && (
        <div className="ra-setting">
          <div className="ra-label">Wake from sleep</div>
          {power.wake.enabled === true && <div className="ra-hint">This computer is set up to wake from a network signal. To use it, keep another of your computers on the same network online.</div>}
          {power.wake.enabled === false && <div className="ra-hint">Wake support requires configuration on this computer.</div>}
          {power.wake.enabled === null && <div className="ra-hint">Brocoded can't tell whether this computer wakes from a network signal.</div>}
          {power.wake.enabled !== true && WAKE_STEPS[power.os] && <div className="ra-note">{WAKE_STEPS[power.os]}</div>}
        </div>
      )}
      <details className="ra-adv">
        <summary>Limits and troubleshooting</summary>
        <ul>
          <li>Keeping available stops idle sleep only. Closing a laptop lid, or choosing Sleep from the menu, still puts it to sleep (a Mac with its lid closed stays awake only with a display, keyboard and power connected).</li>
          <li>A computer that is shut down can't be started this way. Waking needs hardware that supports it and another of your computers on the same network.</li>
          <li>The relay alone can't wake a computer: nothing can reach a sleeping one over the internet.</li>
          {power.os === "mac" && <li>Brocoded asks macOS not to idle-sleep (the same request as the “caffeinate” command). It changes no system setting and needs no password.</li>}
          {power.os === "windows" && <li>Brocoded asks Windows not to idle-sleep (a standard power request). It changes no system setting and needs no administrator rights.</li>}
        </ul>
      </details>
    </div>
  );
}

function Row({ summary, open, onToggle }: { summary: ComputerSummary; open: boolean; onToggle: () => void }) {
  const { hub, store, toast } = useApp();
  const { conn, snap } = useConnection(summary.computer.id);
  const a = describeAvailability(summary.state, summary.wake, summary.wakeSupport);
  const os = snap.power ? OS[snap.power.os] : undefined;
  const pending = conn?.pendingPower();
  const color = a.kind === "ready" ? "var(--ok)" : a.kind === "unauthorized" ? "var(--danger)" : a.kind === "waking" || a.kind === "connecting" ? "var(--warn)" : STATUS.offline.color;
  const id = "ra-" + summary.computer.id.slice(0, 8);
  return (
    <div className="ra-computer">
      <button className="acct-row ra-head" aria-expanded={open} aria-controls={id} onClick={onToggle}>
        <Tile>{YouIcon.laptop}</Tile>
        <span className="acct-row-main">
          <strong>{store.get(summary.computer.id)?.label ?? "Computer"}</strong>
          <span className="ra-status">
            <span className="dot-state" style={{ background: color }} aria-hidden />
            {[os, a.label, pending ? "change pending" : null].filter(Boolean).join(" · ")}
          </span>
        </span>
        <Chevron />
      </button>
      {open && (
        <div className="ra-panel" id={id}>
          <div className="ra-msg" role="status">{a.message}</div>
          {(a.kind === "offline" || a.kind === "wake-failed" || a.kind === "waking") && (
            <>
              {a.kind !== "waking" && a.canWake && (
                <button className="btn secondary ra-wake" onClick={() => void hub.wake(summary.computer.id).catch(() => toast("Couldn't send the wake signal"))}>
                  Wake computer
                </button>
              )}
              {a.kind === "waking" && <div className="ra-msg"><Spinner /> Waiting for it to connect</div>}
              {pending && <div className="ra-note">Your change will apply as soon as this computer is online.</div>}
            </>
          )}
          {summary.state === "ready" && <Settings summary={summary} />}
        </div>
      )}
    </div>
  );
}

/** The Remote Availability card on the You screen: every computer with its real status and the settings it supports. */
export function RemoteAvailability() {
  const { hub } = useApp();
  const summaries = useHubSummaries(hub);
  const [open, setOpen] = useState<string | null>(null);
  return (
    <section aria-labelledby="ra-title">
      <div className="group-label" id="ra-title">Remote availability</div>
      <div className="ra-sub">Control which computers stay ready for remote coding.</div>
      <div className="card ra">
        {summaries.length === 0 && <div className="acct-empty">No computers yet. Pair one from the home screen and its settings appear here.</div>}
        {summaries.map((s) => (
          <Row key={s.computer.id} summary={s} open={open === s.computer.id} onToggle={() => setOpen(open === s.computer.id ? null : s.computer.id)} />
        ))}
      </div>
    </section>
  );
}
